/**
 * Grading › Batches — this month's batches and their queue state (ops mock version 13; ADR-133 Amendment 2 §4,
 * Amendment 3). One batch a month per zone; each zone's figures are listed side by side, never summed. A site releases
 * at batch close with however many accepted grades it has (Amendment 3); with none, it keeps its AI reading and is
 * carried. super_admin may also queue a customer's site NOW ("Queue a batch now", item C): it is built exactly as the
 * monthly job would build it and offered, marked manual in its history.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterPills } from '../../../components/FilterPills';
import { FilterSearch } from '../../../components/FilterSearch';
import { ColumnTooltip } from '../../../components/ColumnTooltip';
import { ZoneBoardLegend } from '../../../components/ZoneBoardLegend';
import { ActionConfirmModal } from '../ActionConfirmModal';
import { useToast, toastFromError } from '../../../components/ToastProvider';
import { zoneBoardLegend, countInZone, matchesZoneFilter, ALL_ZONES, type Zoned } from '../../../lib/zone-board';
import { ZONES, consoleZoneLabel, zoneByCode, type PortalZone } from '../../../lib/zones';
import {
  getSite, listBatches, listCandidates, monthLabel, queueBatchNow,
  type BatchesResponse, type CandidateSite, type SiteDetail, type SiteRow,
} from '../../../lib/grading-api';
import { ActionIcon, Dialog, Loading, fmtDate, useZoneList, type PageCtx } from './shared';
import { ZoneCell } from '../../../components/ZoneIndicators';

type StatusKey = 'done' | 'progress' | 'carried' | 'short';
export function siteStatus(s: Pick<SiteRow, 'state' | 'headStart'>, now = Date.now()): StatusKey {
  if (s.state === 'Released' || s.state === 'Closed') return 'done';
  if (s.state === 'Carried') return 'carried';
  if (s.headStart && Date.parse(s.headStart.until) > now) return 'short';
  return 'progress';
}
const STATUS: Record<StatusKey, [string, string]> = {
  done: ['portal-badge-active', 'Done'], progress: ['portal-badge-pending', 'In progress'],
  carried: ['', 'Carried over'], short: ['portal-badge-paused', 'Short — head start'],
};

function SiteDialog({ zone, siteId, onClose }: { zone: PortalZone; siteId: string; onClose: () => void }) {
  const toast = useToast();
  const [d, setD] = useState<SiteDetail | null>(null);
  useEffect(() => {
    getSite(zone, siteId).then(setD).catch((err) => {
      const { title, detail } = toastFromError(err, 'Could not load the site');
      toast.push({ variant: 'error', title, detail });
      onClose();
    });
  }, [zone, siteId, toast, onClose]);
  return (
    <Dialog wide title={d ? `${d.site.host} · ${d.site.siteId}` : 'Site'} onClose={onClose}>
      {!d ? <Loading text="Loading the site's pages and gradings…" /> : (
        <>
          <p>{d.site.pages} pages — {d.site.kinds.join(' · ')}. {d.site.origin === 'manual' ? <b>Queued manually{d.site.queuedBy ? ` by ${d.site.queuedBy}` : ''}. </b> : null}{d.site.offerOnly ? 'Offered to named graders only (a staff test site). ' : ''}Closes {fmtDate(d.site.closeAt)}.</p>
          <p className="legend-cap">Gradings by grader number — a grader's name is never shown here.</p>
          <div className="tblw"><table className="portal-table">
            <thead><tr><th>Grader</th><th>State</th><th>Answers</th><th>Tells</th><th>Weight</th><th>Submitted</th></tr></thead>
            <tbody>
              {d.grades.length === 0 && <tr><td colSpan={6} className="portal-users-empty">No gradings yet.</td></tr>}
              {d.grades.map((g) => (
                <tr key={g.graderNumber} className={g.quarantined ? 'flag' : undefined}>
                  <td className="num">{g.graderNumber}</td>
                  <td>{g.quarantined ? <span className="portal-badge portal-badge-quar">Quarantined</span> : g.state}</td>
                  <td className="num">{g.bands.map((b) => b ?? '–').join(' ')}</td>
                  <td>{g.tells.length ? g.tells.join(', ') : '—'}</td>
                  <td className="num">{g.weight}</td>
                  <td>{fmtDate(g.submittedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <p className="legend-cap">Offers: {d.offers.length} · open {d.offers.filter((o) => o.state === 'Open').length} · accepted {d.offers.filter((o) => o.state === 'Accepted').length}{d.offers.some((o) => o.headStart) ? ' · some in their head start' : ''}.</p>
        </>
      )}
    </Dialog>
  );
}

/** "Queue a batch now" — super_admin only (can.queue): pick a customer's site, confirm, build and offer it. */
function QueueNow({ zone, onQueued }: { zone: PortalZone; onQueued: () => void }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [cands, setCands] = useState<CandidateSite[] | null>(null);
  const [pick, setPick] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || cands) return;
    listCandidates(zone).then((r) => setCands(r.sites)).catch((err) => {
      const { title, detail } = toastFromError(err, 'Could not load the customer sites');
      toast.push({ variant: 'error', title, detail });
      setOpen(false);
    });
  }, [open, cands, zone, toast]);
  const chosen = cands?.find((c) => `${c.accountId}|${c.domainToken}` === pick) ?? null;
  const run = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      const r = await queueBatchNow(zone, chosen.accountId, chosen.domainToken);
      toast.push({ variant: 'success', title: 'Batch queued', detail: `${chosen.host}: ${r.site.pages} pages queued and offered to ${r.offered} grader${r.offered === 1 ? '' : 's'} in ${consoleZoneLabel(zone.code)}.` });
      setConfirm(false); setOpen(false); setPick(''); setCands(null); onQueued();
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      const reason = code === 'already_built' ? 'That site already has a batch this month.'
        : code === 'no_key_pages' ? 'That site has no key pages with a capture yet — its Website run has not captured them.'
          : code === 'no_visual_change' ? 'None of that site\'s key pages has changed since it was last graded.' : null;
      const t = reason ? { title: 'Not queued', detail: reason } : toastFromError(err, 'Could not queue the batch');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" className="portal-btn" data-testid="queue-now" onClick={() => setOpen(true)}>Queue a batch now</button>
      {open && !confirm && (
        <Dialog title="Queue a batch now" onClose={() => setOpen(false)} actions={<>
          <button type="button" className="portal-link-button-secondary" onClick={() => setOpen(false)}>Cancel</button>
          <button type="button" className="portal-btn" disabled={!chosen || chosen.alreadyThisMonth} onClick={() => setConfirm(true)}>Continue</button>
        </>}>
          <p>Choose a customer's site in {consoleZoneLabel(zone.code)}. It is built exactly as the monthly batch would build it — its key pages with a capture, in the visitor's order — and offered to graders now, marked <b>manual</b> in its history.</p>
          {!cands ? <Loading text="Loading the customer sites…" /> : (
            <label className="portal-users-filter-label">Customer site
              <select className="portal-select" aria-label="Customer site" value={pick} onChange={(e) => setPick(e.target.value)}>
                <option value="">Choose a site…</option>
                {cands.map((c) => <option key={`${c.accountId}|${c.domainToken}`} value={`${c.accountId}|${c.domainToken}`} disabled={c.alreadyThisMonth}>{c.host}{c.alreadyThisMonth ? ' — already in this month\'s batch' : ''}</option>)}
              </select>
            </label>
          )}
        </Dialog>
      )}
      <ActionConfirmModal open={confirm} title="Queue this site now?" intent="primary" primaryLabel="Queue and offer" loading={busy}
        onConfirm={() => void run()} onCancel={() => setConfirm(false)}>
        <p><b>{chosen?.host}</b> is queued in {consoleZoneLabel(zone.code)} and offered to eligible graders now. It closes after the rules' close period and releases with the grades it has.</p>
      </ActionConfirmModal>
    </>
  );
}

export function BatchesTab({ zone, ctx }: { zone: PortalZone; ctx: PageCtx }) {
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [heads, setHeads] = useState<Record<string, BatchesResponse>>({});
  const { rows, results, reload } = useZoneList<SiteRow>(async (z) => {
    const r = await listBatches(z, month);
    setHeads((h) => ({ ...h, [z.code]: r }));
    return r.sites;
  }, 'the batches', [month]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<StatusKey>>(new Set(['done', 'progress', 'carried', 'short']));
  const [zoneFilter, setZoneFilter] = useState(ALL_ZONES);
  const [open, setOpen] = useState<Zoned<SiteRow> | null>(null);
  const closeSite = useCallback(() => setOpen(null), []);
  const all = useMemo(() => rows ?? [], [rows]);
  const head = heads[zone.code] ?? Object.values(heads)[0];
  const months = head?.months ?? [];
  const shown = useMemo(() => all.filter((s) => selected.has(siteStatus(s)) && matchesZoneFilter(s, zoneFilter)
    && (!search.trim() || `${s.host} ${s.siteId}`.toLowerCase().includes(search.trim().toLowerCase()))), [all, selected, zoneFilter, search]);
  const count = (k: StatusKey) => all.filter((s) => siteStatus(s) === k).length;
  const inZone = all.filter((s) => s.zone === zone.code);
  const accepted = inZone.reduce((t, s) => t + s.accepted, 0);

  return (
    <section id="t-batches" role="tabpanel" aria-label="Batches">
      <div className="panel-step">
        <h3>{head ? `${monthLabel(head.month)} batch` : 'This month\'s batch'} <span className="portal-badge">{consoleZoneLabel(zone.code)}</span>
          {ctx.can.queue && <span style={{ marginLeft: 'auto' }}><QueueNow zone={zone} onQueued={() => void reload()} /></span>}</h3>
        <p className="panel-help">One batch a month per zone, built on batch day{head ? ` (day ${head.batchDay})` : ''}, the day after the zone's report window closes. Every customer with a scored site is in; each is <b>one site of its key pages</b> in the visitor's order, each page carrying its <b>kind</b>, which is the only label a grader sees. A site comes back only when its captures differ visually from the last graded ones. Each site is offered to about twelve eligible graders; at eight completed gradings it leaves every other queue. At batch close{head ? `, ${head.closeAfterDays} days after batch day,` : ''} a site is released with <b>however many accepted grades it has</b>; a site with none keeps its AI reading, labelled provisional, and is carried to the next month.</p>
        {rows === null ? <Loading text="Loading this month's batches…" /> : (
          <>
            <div className="batch-head">
              <div className="stat"><b>{inZone.length}</b><span>sites in</span></div>
              <div className="stat"><b>{inZone.filter((s) => ['Queued', 'Offered', 'Grading'].includes(s.state)).length}</b><span>in the queue</span></div>
              <div className="stat"><b>{inZone.filter((s) => s.state === 'Released').length}</b><span>released</span></div>
              <div className="stat"><b>{inZone.filter((s) => s.state === 'Carried').length}</b><span>carried over</span></div>
              <div className="stat"><b>{inZone.filter((s) => siteStatus(s) === 'short').length}</b><span>in their head start</span></div>
              <div className="stat"><b>{inZone.filter((s) => s.origin === 'manual').length}</b><span>queued manually</span></div>
            </div>
            <div className="prog"><div className="bar" aria-hidden="true"><i style={{ width: `${inZone.length ? Math.min(100, Math.round((accepted / (inZone.length * 8)) * 100)) : 0}%` }} /></div><span><b style={{ color: '#f1f5f9' }}>{accepted}</b> accepted gradings in · {consoleZoneLabel(zone.code)}</span></div>
          </>
        )}
      </div>
      <div className="panel-step">
        <h3>Sites in this batch</h3>
        <div className="portal-applications-controls">
          <FilterSearch value={search} onChange={setSearch} placeholder="Search site or site id…" ariaLabel="Search batch sites" />
          <FilterPills ariaLabel="Filter sites by batch status" selected={selected} onChange={setSelected}
            groups={[{ key: 'done', label: 'Done', icon: '✅', count: count('done') }, { key: 'progress', label: 'In progress', icon: '⏳', count: count('progress') }, { key: 'carried', label: 'Carried over', icon: '↩️', count: count('carried') }, { key: 'short', label: 'Short — head start', icon: '⚠️', count: count('short') }]} />
        </div>
        <div className="portal-users-filters">
          <label className="portal-users-filter-label">Zone<select className="portal-select" aria-label="Zone filter" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
            <option value={ALL_ZONES}>All zones</option>{ZONES.map((z) => <option key={z.code} value={z.code}>{z.label}</option>)}</select></label>
          <label className="portal-users-filter-label">Month<select className="portal-select" aria-label="Month" value={month ?? head?.month ?? ''} onChange={(e) => setMonth(e.target.value)}>
            {(months.length ? months : head ? [head.month] : []).map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}</select></label>
        </div>
        <ZoneBoardLegend testId="batches-legend" ariaLabel="Batch by zone" loading={rows === null}
          columns={[{ key: 'sites', label: 'Sites in' }, { key: 'live', label: 'In the queue' }, { key: 'released', label: 'Released' }, { key: 'carried', label: 'Carried' }]}
          rows={zoneBoardLegend(results, { counts: (code) => ({ sites: countInZone(all, code), live: countInZone(all, code, (s) => ['Queued', 'Offered', 'Grading'].includes(s.state)), released: countInZone(all, code, (s) => s.state === 'Released'), carried: countInZone(all, code, (s) => s.state === 'Carried') }) })} />
        <p className="legend-cap">Each zone's batch is its own; the figures are listed side by side, not summed.</p>
        {rows !== null && (
          <div className="tblw"><table className="portal-table" id="tbl-batches">
            <thead><tr><th>Site</th><th>Zone</th><th>Pages</th>
              <th><span className="portal-col-help-wrap">Kinds<ColumnTooltip columnName="Kinds" items={[{ term: 'Kinds', desc: "The page kinds in the visitor's order — the only label a grader sees." }]} /></span></th>
              <th>Offered</th><th>Accepted</th><th>Result</th>
              <th><span className="portal-col-help-wrap">Status<ColumnTooltip columnName="Status" items={[{ term: 'Done', desc: 'Released, or withdrawn at eight accepted grades.' }, { term: 'In progress', desc: 'In the queue, offered to graders.' }, { term: 'Carried over', desc: 'Closed with no accepted grade — keeps its AI reading, back in next month.' }, { term: 'Short — head start', desc: 'The groups it is short of see it first.' }]} /></span></th>
              <th>History</th><th>Actions</th></tr></thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={10} className="portal-users-empty">{selected.size === 0 ? 'No filter pills selected — click a batch-status pill to show sites.' : 'No sites in this batch.'}</td></tr>}
              {shown.map((s) => {
                const k = siteStatus(s); const [cls, word] = STATUS[k];
                return (
                  <tr key={`${s.zone}:${s.siteId}`} data-status={k}>
                    <td>{s.host} <code>{s.siteId}</code></td>
                    <td><ZoneCell zone={s.zone} /></td>
                    <td className="num">{s.pages}</td>
                    <td>{s.kinds.join(' · ')}</td>
                    <td className="num">{s.offered}</td>
                    <td className="num">{s.accepted}{s.withdrawnAt ? ' · closed' : ''}</td>
                    <td>{s.result ? `${s.result.band} · ${s.result.graders} grader${s.result.graders === 1 ? '' : 's'}` : s.state === 'Carried' ? 'AI reading (provisional)' : '—'}</td>
                    <td><span className={`portal-badge ${cls}`}>{word}</span></td>
                    <td>{s.origin === 'manual' ? <><b>manual</b>{s.queuedBy ? ` · ${s.queuedBy}` : ''} · </> : null}queued {fmtDate(s.queuedAt)}{s.carriedFrom ? ' · carried in' : ''}{s.releasedAt ? ` · released ${fmtDate(s.releasedAt)}` : ''}</td>
                    <td className="portal-actions-col"><div className="portal-actions-row">
                      <ActionIcon act="view" icon="view" label={`View ${s.host}'s pages and gradings`} tip="View this site's pages and gradings" onClick={() => setOpen(s)} />
                    </div></td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
        <p className="panel-help" style={{ margin: '.75rem 0 0' }}>A site's row names the business — staff see it; a grader sees the pages only, by their kinds.</p>
      </div>
      {open && <SiteDialog zone={zoneByCode(open.zone)!} siteId={open.siteId} onClose={closeSite} />}
    </section>
  );
}
